import {
  useWebAppUpdateTranscriptionWordsFragmentDetail,
  webAppGetFragmentDetailTranscriptionQueryKey,
} from '@mi-front/api-v2';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { buildTranscriptionWordsPayload } from '../lib/build-transcription-words-payload';
import { findTranscriptionWord } from '../lib/find-transcription-word';
import { TranscriptionBlockData } from '../lib/transcription.types';

const saveErrorMessage = defineMessage({
  id: 'fragments.transcription.v2.editSaveError',
  defaultMessage: 'No se pudo guardar la palabra. Intentá de nuevo.',
});

type TranscriptionWordEditCapture = {
  onEditStart: (params: { wordStartSeconds: number; originalTextLength: number }) => void;
  onEditConfirm: (params: {
    wordStartSeconds: number;
    textChanged: boolean;
    originalTextLength: number;
    newTextLength: number;
  }) => void;
  onEditCancel: (params: {
    wordStartSeconds: number;
    textChanged: boolean;
    originalTextLength: number;
    draftTextLength: number;
  }) => void;
};

type UseTranscriptionWordEditingParams = {
  fragmentId: string;
  blocks: TranscriptionBlockData[];
  capture?: TranscriptionWordEditCapture;
};

/**
 * Manages inline word editing state and persists changes via the transcription-words mutation.
 */
export function useTranscriptionWordEditing({
  fragmentId,
  blocks,
  capture,
}: UseTranscriptionWordEditingParams) {
  const queryClient = useQueryClient();
  const { $t } = useIntl();

  const [editingWordId, setEditingWordId] = useState<string | null>(null);
  const [draftText, setDraftText] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const originalTextRef = useRef('');

  const { mutate, isPending: isSaving } = useWebAppUpdateTranscriptionWordsFragmentDetail({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(webAppGetFragmentDetailTranscriptionQueryKey(fragmentId), data);
        setEditingWordId(null);
        setDraftText('');
        setSaveError(null);
        originalTextRef.current = '';
      },
      onError: () => {
        setSaveError($t(saveErrorMessage));
      },
    },
  });

  const getEditingWord = useCallback(
    (wordId: string) => findTranscriptionWord(blocks, wordId),
    [blocks],
  );

  const startEditing = useCallback(
    (wordId: string, text: string) => {
      const word = getEditingWord(wordId);
      originalTextRef.current = text;
      setEditingWordId(wordId);
      setDraftText(text);
      setSaveError(null);

      if (word && capture) {
        capture.onEditStart({
          wordStartSeconds: word.start,
          originalTextLength: text.length,
        });
      }
    },
    [capture, getEditingWord],
  );

  const cancelEditing = useCallback(() => {
    if (editingWordId && capture) {
      const word = getEditingWord(editingWordId);
      const originalText = originalTextRef.current;

      if (word) {
        capture.onEditCancel({
          wordStartSeconds: word.start,
          textChanged: draftText.trim() !== originalText.trim(),
          originalTextLength: originalText.length,
          draftTextLength: draftText.trim().length,
        });
      }
    }

    setEditingWordId(null);
    setDraftText('');
    setSaveError(null);
    originalTextRef.current = '';
  }, [capture, draftText, editingWordId, getEditingWord]);

  const saveEditing = useCallback(() => {
    if (!editingWordId || isSaving) {
      return;
    }

    const trimmed = draftText.trim();
    if (!trimmed) {
      return;
    }

    const word = getEditingWord(editingWordId);
    const originalText = originalTextRef.current;

    if (word && capture) {
      capture.onEditConfirm({
        wordStartSeconds: word.start,
        textChanged: trimmed !== originalText.trim(),
        originalTextLength: originalText.length,
        newTextLength: trimmed.length,
      });
    }

    const words = buildTranscriptionWordsPayload(blocks, editingWordId, trimmed);
    mutate({ id: fragmentId, data: { words } });
  }, [blocks, capture, draftText, editingWordId, fragmentId, getEditingWord, isSaving, mutate]);

  return {
    editingWordId,
    draftText,
    setDraftText,
    startEditing,
    cancelEditing,
    saveEditing,
    isSaving,
    saveError,
  };
}
